/**
 * Help Search Library
 * Provides help query detection and article search functionality
 * for AI assistant integration
 */

export interface HelpArticle {
  id: string
  title: string
  slug: string
  category: string
  content: string
  summary: string
  keywords: string[]
}

export interface HelpSearchResult {
  results: HelpArticle[]
}

// Patterns that indicate a user is asking for help
const HELP_PATTERNS = [
  /how do i/i,
  /how can i/i,
  /how to/i,
  /where is/i,
  /where can i find/i,
  /help me/i,
  /show me how/i,
  /walk me through/i,
  /what is the/i,
  /getting started/i,
  /can you show me/i,
  /teach me/i,
  /explain how/i,
  /steps to/i,
  /guide me/i,
  /i need help/i,
  /i want to/i,
  /i'm trying to/i,
  /how would i/i,
  /what's the best way to/i
]

/**
 * Detects if a message is asking for help/guidance
 * @param message - The user's message
 * @returns true if the message appears to be asking for help
 */
export function detectHelpQuery(message: string): boolean {
  return HELP_PATTERNS.some(pattern => pattern.test(message))
}

/**
 * Searches for relevant help articles
 * @param query - The search query
 * @param limit - Maximum number of results (default 3)
 * @returns Promise resolving to array of help articles
 */
export async function searchHelp(query: string, limit = 3): Promise<HelpArticle[]> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

    const response = await fetch(`${supabaseUrl}/functions/v1/help-search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${anonKey}`
      },
      body: JSON.stringify({ query, limit })
    })

    if (!response.ok) {
      console.error('Help search failed:', response.status, response.statusText)
      return []
    }

    const data: HelpSearchResult = await response.json()
    return data.results || []
  } catch (error) {
    console.error('Help search error:', error)
    return []
  }
}

/**
 * Builds a help context string from articles for AI injection
 * @param articles - Array of help articles
 * @returns Formatted context string
 */
export function buildHelpContext(articles: HelpArticle[]): string {
  if (articles.length === 0) {
    return ''
  }

  return articles
    .map(article => `## ${article.title}\n\n${article.content}`)
    .join('\n\n---\n\n')
}

/**
 * Builds an enhanced message with help context for the AI
 * @param originalMessage - The user's original message
 * @param helpContext - The help article context
 * @returns Enhanced message with instructions
 */
export function buildHelpEnhancedMessage(
  originalMessage: string,
  helpContext: string
): string {
  return `The user is asking for help. Here are relevant articles from our documentation:

${helpContext}

Please answer the user's question using the information above. Format your response with:
1. Step-by-step instructions (numbered)
2. Use **bold** for button names and UI elements
3. Be concise but complete
4. If the articles don't fully answer the question, provide general guidance
5. End with helpful links using this format:
   - For relevant article: "Read full guide" link
   - Always include: "Still stuck? Contact Support"

User question: ${originalMessage}`
}

/**
 * Generates a help response footer with links
 * @param article - The primary help article used
 * @returns Formatted footer string
 */
export function buildHelpFooter(article: HelpArticle | null): string {
  const parts: string[] = []

  if (article) {
    parts.push(`\n\n---\n\n[Read full guide](/help/${article.category}/${article.slug})`)
  }

  parts.push(`\n\n[Still stuck? Contact Support](mailto:support@aifacilitymanager.com)`)

  return parts.join('')
}
